import {
  TimeOffChangedFieldResponse,
  TimeOffTypeChangeResponse,
} from '../types/dto/response/time-off-type-change-response';
import { TimeOffChangedField, TimeOffTypeChangeLogEntry } from '../types/time-off-changes';

/**
 * Coerces a raw DTO value (object | number | string | null) to a display string.
 */
const stringifyValue = (value: unknown | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  return String(value);
};

/**
 * Resolves the display value, preferring the localizable value key over the raw value.
 */
const resolveValue = (
  value: unknown | null | undefined,
  valueKey: string | null | undefined
): string | null => valueKey ?? stringifyValue(value);

/**
 * Maps a single changed field from DTO to domain model.
 */
const mapChangedField = (field: TimeOffChangedFieldResponse): TimeOffChangedField => ({
  field: field.fieldName,
  fieldLocalizedName: null,
  from: resolveValue(field.oldValue, field.oldValueKey),
  to: resolveValue(field.newValue, field.newValueKey),
  subfieldChanges: null,
});

/**
 * Maps a time-off type change from API response to domain model.
 */
export const mapTimeOffTypeChangeFromResponse = (
  response: TimeOffTypeChangeResponse
): TimeOffTypeChangeLogEntry => ({
  id: response.id,
  startDate: response.effectiveStartDate,
  endDate: response.effectiveEndDate,
  isActive: response.isActive,
  allowedActions: response.allowedActions,
  changedBy: response.changedByUser
    ? {
        id: response.changedByUser.id,
        name: response.changedByUser.name,
        profileImageUrl: response.changedByUser.profileImageUrl ?? null,
      }
    : null,
  changedFields: response.changedFields.map(mapChangedField),
  isInitialChange: response.isInitialChange,
});

/**
 * Maps multiple time-off type changes from API response to domain models.
 */
export const mapTimeOffTypeChangesFromResponse = (
  responses: TimeOffTypeChangeResponse[]
): TimeOffTypeChangeLogEntry[] => responses.map(mapTimeOffTypeChangeFromResponse);
